import { useMemo, useRef, useState } from 'react'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { SecondaryButton, Typography } from '@musinsa/layer-components-desktop'
import { HStack } from '@musinsa/mwms-ds'
import dayjs from 'dayjs'
import { PricingTableStyle } from '../pricingGridStyle'
import GridTableV2 from '@/components/tableV2/GridTableV2'
import { Pagination } from '@/components/tableV2/GridTableV2Toolbar'
import { usePagination } from '@/hooks/usePagination'
import { DATE_FORMAT } from '@/utils/consts'
import { Utils } from '@/utils/Utils'
import { GRID_SIZE } from '@/utils/grid/grid'
import { BasePriceListRes } from '@/services/settlement/settlementPricing/types'
import { BasePriceHistoryModal } from './BasePriceHistoryModal'

interface Props {
  rowData: BasePriceListRes[]
  pageInfo: PageInfoType
  onChangePage: (page: number) => void
  onChangePageSize: (size: number) => void
  isLoading?: boolean
}

const price = (v?: number | null) => (v || v === 0 ? `${Utils.getCommaNum(v)}원` : '-')
const date = (v?: string | null) => (v ? dayjs(v).format(DATE_FORMAT.date) : '-')

// 기본단가는 읽기 전용 마스터 테이블 — 변경예약(단가 관리/수정·시작/종료 스케줄)은 미제공
export function BasePriceTable({ rowData, pageInfo, onChangePage, onChangePageSize, isLoading }: Props) {
  const gridRef = useRef<AgGridReact>(null)
  const [historyMasterId, setHistoryMasterId] = useState<number | null>(null)

  const size = pageInfo.size || GRID_SIZE.SETTLEMENT
  const page = pageInfo.page || 0
  const totalCount = pageInfo.totalCount || 0

  const paginationProps = usePagination({
    totalCount,
    currentPage: page,
    pageSize: size,
    onChangePage,
    onPageSizeChange: onChangePageSize,
  })

  const columnDefs = useMemo<ColDef<BasePriceListRes>[]>(
    () => [
      { colId: 'country', headerName: '국가', width: 90, valueGetter: ({ data }) => data?.country?.name ?? '-' },
      { colId: 'serviceType', headerName: '서비스 이용 항목', width: 140, valueGetter: ({ data }) => data?.serviceType?.name ?? '-' },
      // LP9878 — 송장 행(charge_base_type=INVOICE)은 '송장', 그 외는 unit_type 명(PCS/box). chargeBaseType 미도래(BE 롤아웃 전) 시 unitType fallback.
      { colId: 'unitType', headerName: '단위', width: 80, valueGetter: ({ data }) => (data?.chargeBaseType?.code === 'INVOICE' ? '송장' : (data?.unitType?.name ?? '-')) },
      { colId: 'orderType', headerName: '주문유형', width: 90, valueGetter: ({ data }) => data?.orderType?.name ?? '-' },
      { field: 'currentPrice', headerName: '현재 단가', width: 120, valueFormatter: ({ value }) => price(value) },
      { field: 'updatedBy', headerName: '수정자', width: 180, valueFormatter: ({ value }) => value || '-' },
      { field: 'updatedAt', headerName: '수정일', width: 120, valueFormatter: ({ value }) => date(value) },
      {
        colId: 'history',
        headerName: '변경 이력',
        width: 120,
        sortable: false,
        headerClass: 'col-divider',
        cellClass: 'col-divider',
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        // eslint-disable-next-line react/no-unstable-nested-components
        cellRenderer: ({ data }: ICellRendererParams<BasePriceListRes>) =>
          data ? <SecondaryButton variant="outlined" size="small" label="변경 이력" onClick={() => setHistoryMasterId(data.id)} /> : null,
      },
      // 남는 폭 채움(빈 스페이서)
      { headerName: '', flex: 1, minWidth: 0, sortable: false },
    ],
    [],
  )

  return (
    <>
      <PricingTableStyle />
      <GridTableV2<BasePriceListRes>
        gridKey="BL04_BASE"
        ref={gridRef}
        idKey="id"
        className="pricing-grid"
        rowData={rowData}
        isLoading={isLoading}
        gridOptions={{ rowHeight: 52 }}
        columnDefs={columnDefs}
        totalCount={totalCount}
        rowStartIndex={page * size}
        useColumnNo
        showTableHeader={false}
        noRowsOverlayComponentParams={{ mainContent: '조회 결과가 없습니다.' }}
        title={
          <>
            <HStack gap={4} align="center">
              <Typography name="base-text-m" colorToken="fg">
                전체
              </Typography>
              <Typography name="base-text-m-semibold" colorToken="fg">
                {totalCount}
              </Typography>
            </HStack>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <Pagination {...paginationProps} />
            </div>
          </>
        }
      />

      <BasePriceHistoryModal masterId={historyMasterId} onClose={() => setHistoryMasterId(null)} />
    </>
  )
}
